import { useState } from "react";

import { CheckIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

import { Button } from "../base/Button";
import { Select } from "../base/Select";
import SortableList from "./SortableList";
import {
  ALL_REMOVAL_STEPS,
  TITLE_REMOVAL_STEP_LABELS,
  TitleRemovalStep,
} from "../../models/Category/TitleConfig";

interface TitleBuildingRulesProps {
  removalOrder: TitleRemovalStep[];
  characterLimit: number;
  onChange: (removalOrder: TitleRemovalStep[]) => void;
}

const ADD_RULES = [
  "Add fields from top to bottom until the character limit is reached.",
  "Only add a field if data exists and it is relevant.",
  "Synonyms are added one by one in the order listed.",
  "Aspects are added in the order received.",
];

export default function TitleBuildingRules({
  removalOrder,
  characterLimit,
  onChange,
}: TitleBuildingRulesProps) {
  const [stepToAdd, setStepToAdd] = useState<TitleRemovalStep | "">("");

  const used = new Set(removalOrder);
  const available = ALL_REMOVAL_STEPS.filter((step) => !used.has(step));

  const removeStep = (step: TitleRemovalStep) =>
    onChange(removalOrder.filter((s) => s !== step));

  const addStep = () => {
    if (!stepToAdd) return;

    onChange([...removalOrder, stepToAdd]);
    setStepToAdd("");
  };

  return (
    <section className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-3">
        Title Building Rules
      </h2>

      <div className="rounded border border-green-200 bg-green-50/60 p-3 mb-4">
        <h3 className="text-sm font-medium text-green-800 mb-2">Add Fields</h3>
        <ul className="space-y-1.5">
          {ADD_RULES.map((rule) => (
            <li key={rule} className="flex gap-2 text-xs text-gray-700">
              <CheckIcon className="h-4 w-4 shrink-0 text-green-600" />
              <span>{rule}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded border border-red-200 bg-red-50/50 p-3">
        <h3 className="text-sm font-medium text-red-800 mb-1">
          Remove Fields (if over {characterLimit})
        </h3>
        <p className="text-xs text-gray-600 mb-2">
          Applied in order, stopping as soon as the title fits. Mandatory
          fields are never removed.
        </p>

        <SortableList
          items={removalOrder}
          getKey={(step) => step}
          onReorder={onChange}
          emptyText="No removal steps. The title will not be shortened automatically."
          renderItem={(step) => (
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm truncate">
                {TITLE_REMOVAL_STEP_LABELS[step]}
              </span>
              <button
                type="button"
                aria-label={`Remove step ${TITLE_REMOVAL_STEP_LABELS[step]}`}
                onClick={() => removeStep(step)}
                className="shrink-0 text-gray-400 hover:text-red-600"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        />

        {available.length > 0 && (
          <div className="mt-3 flex items-end gap-2">
            <Select
              small
              fullWidth
              label="Add a step"
              value={stepToAdd}
              onChange={(e) =>
                setStepToAdd(e.currentTarget.value as TitleRemovalStep)
              }
            >
              <option value="">Select a step…</option>
              {available.map((step) => (
                <option key={step} value={step}>
                  {TITLE_REMOVAL_STEP_LABELS[step]}
                </option>
              ))}
            </Select>
            <Button
              size="sm"
              variant="outlined"
              onClick={addStep}
              disabled={!stepToAdd}
            >
              <span className="flex items-center gap-1">
                <PlusIcon className="h-4 w-4" /> Add
              </span>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
